import React, { useEffect, useState } from 'react';
import {
  BarChart3, Building2, FileSpreadsheet, Mail,
  Package, BrainCircuit, ChevronRight, CheckCircle2,
  Globe, TrendingUp, Shield, Zap, LayoutDashboard, Disc3, Mic2, Music,
} from 'lucide-react';

// ---- SVG 로고 마크 ----
function KDistLogoMark({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" rx="8" fill="#0ea5e9"/>
      <rect x="5" y="19" width="5" height="8" rx="1.5" fill="white" fillOpacity="0.55"/>
      <rect x="13.5" y="14" width="5" height="13" rx="1.5" fill="white"/>
      <rect x="22" y="9" width="5" height="18" rx="1.5" fill="white" fillOpacity="0.55"/>
      <path d="M7.5 19 L16 13.5 L24.5 8.5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="7.5" cy="19" r="1.8" fill="white"/>
      <circle cx="16" cy="13.5" r="1.8" fill="white"/>
      <circle cx="24.5" cy="8.5" r="2.2" fill="white"/>
    </svg>
  );
}

// ---- 시각화: 월별 정산 리포트 (바 차트) ----
function VizRevenue() {
  const [show, setShow] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShow(true), 80); return () => clearTimeout(t); }, []);
  const bars = [
    { label: '1월', v: 44, val: '280만' },
    { label: '2월', v: 65, val: '415만' },
    { label: '3월', v: 58, val: '370만' },
    { label: '4월', v: 80, val: '510만' },
    { label: '5월', v: 74, val: '470만' },
    { label: '6월', v: 100, val: '638만' },
  ];
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-slate-500">최근 6개월 정산 수익</span>
        <span className="text-xs font-bold text-emerald-400" style={{ opacity: show ? 1 : 0, transition: 'opacity 0.5s ease 0.6s' }}>↑ 34% 전월 대비</span>
      </div>
      <div className="flex items-end gap-2" style={{ height: 110 }}>
        {bars.map((b, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <span className="text-[9px] text-slate-500"
              style={{ opacity: show ? 1 : 0, transition: `opacity 0.4s ease ${i * 0.08 + 0.5}s` }}>
              {b.val}
            </span>
            <div className="w-full rounded-t-md"
              style={{
                height: show ? `${b.v}%` : '0%',
                background: i === 5 ? '#0ea5e9' : `rgba(14,165,233,${0.25 + i * 0.1})`,
                transition: `height 0.65s cubic-bezier(0.34,1.56,0.64,1) ${i * 0.07}s`,
                maxHeight: 90,
              }}
            />
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        {bars.map((b) => (
          <div key={b.label} className="flex-1 text-center text-[9px] text-slate-600">{b.label}</div>
        ))}
      </div>
      <div className="flex gap-3 pt-1 border-t border-white/[0.06]">
        {[['총 수익', '2,683만원'], ['평균', '447만원'], ['최고', '638만원']].map(([l, v]) => (
          <div key={l} className="flex-1 text-center"
            style={{ opacity: show ? 1 : 0, transition: `opacity 0.5s ease 0.8s` }}>
            <div className="text-[10px] text-slate-500">{l}</div>
            <div className="text-xs font-bold text-white">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- 시각화: AI 정산 분석 (도넛 + 인사이트) ----
function VizAI() {
  const [show, setShow] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShow(true), 80); return () => clearTimeout(t); }, []);
  const r = 36;
  const circ = 2 * Math.PI * r;
  const segs = [
    { label: '스트리밍', pct: 63, color: '#0ea5e9' },
    { label: '다운로드', pct: 22, color: '#8b5cf6' },
    { label: '기타', pct: 15, color: '#475569' },
  ];
  let cum = 0;
  const arcs = segs.map((s) => {
    const len = (s.pct / 100) * circ;
    const a = { ...s, len, off: -cum };
    cum += len;
    return a;
  });
  const insights = [
    { label: '수익 집중도', val: '68%', color: 'text-sky-400' },
    { label: '성장 트렌드', val: '+24%', color: 'text-emerald-400' },
    { label: '유휴곡 탐지', val: '12건', color: 'text-amber-400' },
  ];
  return (
    <div className="flex gap-5 items-start">
      <div className="relative shrink-0">
        <svg width="88" height="88" viewBox="0 0 88 88">
          <circle cx="44" cy="44" r={r} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="10"/>
          {arcs.map((arc, i) => (
            <circle key={i} cx="44" cy="44" r={r}
              fill="none" stroke={arc.color} strokeWidth="10"
              strokeDasharray={`${show ? arc.len : 0} ${circ}`}
              strokeDashoffset={arc.off}
              transform="rotate(-90 44 44)"
              style={{ transition: `stroke-dasharray 0.9s cubic-bezier(0.4,0,0.2,1) ${i * 0.18}s` }}
            />
          ))}
          <circle cx="44" cy="44" r="26" fill="#0d1526"/>
          <text x="44" y="41" textAnchor="middle" fontSize="9" fill="#94a3b8">AI</text>
          <text x="44" y="53" textAnchor="middle" fontSize="9" fill="#94a3b8">분석</text>
        </svg>
      </div>
      <div className="flex-1 space-y-2">
        {segs.map((s, i) => (
          <div key={i} className="flex items-center gap-2"
            style={{ opacity: show ? 1 : 0, transition: `opacity 0.4s ease ${i * 0.12 + 0.3}s` }}>
            <div className="w-2 h-2 rounded-full shrink-0" style={{ background: s.color }}/>
            <span className="text-[11px] text-slate-400 flex-1">{s.label}</span>
            <span className="text-[11px] font-bold text-white">{s.pct}%</span>
          </div>
        ))}
        <div className="pt-2 border-t border-white/[0.06] space-y-1.5">
          {insights.map((ins, i) => (
            <div key={i} className="flex justify-between text-[11px]"
              style={{ opacity: show ? 1 : 0, transition: `opacity 0.4s ease ${i * 0.1 + 0.7}s` }}>
              <span className="text-slate-500">{ins.label}</span>
              <span className={`font-bold ${ins.color}`}>{ins.val}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---- 시각화: 권리사 관리 (테이블) ----
function VizClients() {
  const [show, setShow] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShow(true), 80); return () => clearTimeout(t); }, []);
  const clients = [
    { name: '블루웨일 뮤직', amount: '2,140만', trend: '+12%', up: true, w: 90 },
    { name: '선라이즈 엔터', amount: '1,830만', trend: '+8%', up: true, w: 77 },
    { name: '오픈사운드', amount: '960만', trend: '0%', up: null, w: 40 },
    { name: '뮤직팩토리', amount: '720만', trend: '-3%', up: false, w: 30 },
  ];
  return (
    <div className="space-y-1.5">
      <div className="flex text-[10px] text-slate-600 px-2 mb-2">
        <span className="flex-1">권리사</span><span className="w-16 text-right">이번달</span><span className="w-10 text-right">증감</span>
      </div>
      {clients.map((c, i) => (
        <div key={i} className="px-2 py-2 rounded-lg bg-white/[0.04]"
          style={{ opacity: show ? 1 : 0, transform: show ? 'none' : 'translateX(12px)', transition: `all 0.45s ease ${i * 0.09}s` }}>
          <div className="flex items-center mb-1.5">
            <span className="flex-1 text-[11px] font-medium text-slate-300">{c.name}</span>
            <span className="w-16 text-right text-[11px] font-bold text-white">{c.amount}</span>
            <span className={`w-10 text-right text-[11px] font-bold ${c.up === true ? 'text-emerald-400' : c.up === false ? 'text-rose-400' : 'text-slate-500'}`}>{c.trend}</span>
          </div>
          <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
            <div className="h-full rounded-full bg-sky-500/50 transition-all duration-700"
              style={{ width: show ? `${c.w}%` : '0%', transitionDelay: `${i * 0.09 + 0.2}s` }}/>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- 시각화: 음반 정산 (수평 바) ----
function VizPhysical() {
  const [show, setShow] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShow(true), 80); return () => clearTimeout(t); }, []);
  const items = [
    { type: 'CD', stock: 340, sold: 1240, maxSold: 1240, color: '#0ea5e9', bg: 'bg-sky-500' },
    { type: 'LP', stock: 85, sold: 430, maxSold: 1240, color: '#8b5cf6', bg: 'bg-violet-500' },
    { type: '굿즈', stock: 220, sold: 670, maxSold: 1240, color: '#f59e0b', bg: 'bg-amber-500' },
  ];
  return (
    <div className="space-y-5">
      {items.map((item, i) => (
        <div key={i} style={{ opacity: show ? 1 : 0, transition: `opacity 0.4s ease ${i * 0.12}s` }}>
          <div className="flex justify-between text-[11px] mb-1.5">
            <span className="font-semibold text-slate-300">{item.type}</span>
            <span className="text-slate-500">재고 <span className="text-white font-medium">{item.stock}</span> · 판매 <span className="text-white font-medium">{item.sold.toLocaleString()}</span></span>
          </div>
          <div className="h-5 rounded-md bg-white/[0.06] overflow-hidden">
            <div className="h-full rounded-md flex items-center px-2 text-[9px] text-white font-bold transition-all duration-800 ease-out"
              style={{
                width: show ? `${(item.sold / item.maxSold) * 100}%` : '0%',
                background: item.color,
                transition: `width 0.8s cubic-bezier(0.34,1.2,0.64,1) ${i * 0.15}s`,
                opacity: 0.85,
              }}>
              {item.sold}
            </div>
          </div>
        </div>
      ))}
      <div className="text-[10px] text-slate-600 pt-1">총 판매: <span className="text-slate-400 font-medium">2,340개</span></div>
    </div>
  );
}

// ---- 시각화: 정산서 발송 ----
function VizEmail() {
  const [show, setShow] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShow(true), 80); return () => clearTimeout(t); }, []);
  const emails = [
    '블루웨일 뮤직', '선라이즈 엔터테인먼트', '오픈사운드', '뮤직팩토리', '비트랩 스튜디오',
  ];
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] text-slate-500 mb-3 flex justify-between">
        <span>2024년 6월 정산서 일괄 발송</span>
        <span className="text-emerald-400 font-semibold" style={{ opacity: show ? 1 : 0, transition: 'opacity 0.4s ease 0.9s' }}>5/5 완료</span>
      </div>
      {emails.map((e, i) => (
        <div key={i} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg bg-white/[0.04]"
          style={{ opacity: show ? 1 : 0, transform: show ? 'none' : 'translateY(6px)', transition: `all 0.35s ease ${i * 0.12}s` }}>
          <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
            style={{ background: 'rgba(52,211,153,0.15)', opacity: show ? 1 : 0, transition: `opacity 0.4s ease ${i * 0.12 + 0.25}s` }}>
            <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 5L4 7L8 3" stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" fill="none"/></svg>
          </div>
          <span className="text-[11px] text-slate-300 flex-1">{e}</span>
          <span className="text-[10px] text-slate-600">발송 완료</span>
        </div>
      ))}
    </div>
  );
}

// ---- 시각화: 엑셀 대량 등록 (업로드 + 결과) ----
function VizUpload() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    let p = 0;
    const id = setInterval(() => {
      p = Math.min(p + 2.5, 100);
      setProgress(p);
      if (p >= 100) clearInterval(id);
    }, 35);
    return () => clearInterval(id);
  }, []);
  const total = 247;
  const processed = Math.floor(progress * total / 100);
  const errors = Math.min(Math.floor(processed * 0.024), 6);
  const success = processed - errors;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-[11px] text-slate-400">
        <div className="w-7 h-7 rounded-lg bg-emerald-500/10 flex items-center justify-center">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="2" y="2" width="10" height="10" rx="1.5" stroke="#34d399" strokeWidth="1.2"/><path d="M5 7L6.5 8.5L9 5.5" stroke="#34d399" strokeWidth="1.2" strokeLinecap="round"/></svg>
        </div>
        아티스트_앨범_일괄등록.xlsx
      </div>
      <div>
        <div className="flex justify-between text-[11px] mb-1.5">
          <span className="text-slate-400">{progress < 100 ? '처리 중...' : '완료'}</span>
          <span className="text-white font-bold">{Math.floor(progress)}%</span>
        </div>
        <div className="h-2 rounded-full bg-white/[0.08] overflow-hidden">
          <div className="h-full rounded-full transition-none"
            style={{ width: `${progress}%`, background: progress < 100 ? '#0ea5e9' : '#34d399' }}/>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: '전체', value: total, color: 'text-white' },
          { label: '성공', value: success, color: 'text-emerald-400' },
          { label: '오류', value: errors, color: errors > 0 ? 'text-rose-400' : 'text-slate-600' },
        ].map((s) => (
          <div key={s.label} className="rounded-xl bg-white/[0.04] p-2 text-center">
            <div className={`text-xl font-black ${s.color}`}>{s.value}</div>
            <div className="text-[10px] text-slate-600">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const FEATURES = [
  { icon: BarChart3, title: '정산 리포트', desc: '권리사·앨범·곡·플랫폼별 매출을 한눈에. 월별 트렌드와 수익 집계를 실시간으로 확인합니다.', Viz: VizRevenue },
  { icon: BrainCircuit, title: 'AI 정산 분석', desc: '수익 집중도, 유휴 곡 탐지, 플랫폼 성과 비교까지 — AI가 정산 데이터에서 인사이트를 뽑아냅니다.', Viz: VizAI },
  { icon: Building2, title: '권리사 관리', desc: '멀티 권리사 계정을 통합 관리하고, 각 권리사가 본인 데이터만 확인하도록 접근 권한을 제어합니다.', Viz: VizClients },
  { icon: Package, title: '음반 정산', desc: 'CD·LP·굿즈 등 실물 음반의 입출고, 재고, 판매 정산을 음원과 함께 통합 관리합니다.', Viz: VizPhysical },
  { icon: Mail, title: '정산서 자동 발송', desc: '월정산 마감 후 권리사별로 개인화된 정산서를 이메일로 일괄 발송합니다.', Viz: VizEmail },
  { icon: FileSpreadsheet, title: '엑셀 대량 등록', desc: '권리사·아티스트·앨범·음반 데이터를 엑셀로 일괄 업로드. 초기 마이그레이션도 빠르게 완료합니다.', Viz: VizUpload },
];

const HOW_IT_WORKS = [
  { step: '01', title: '도입 상담', desc: '서비스 규모와 요구사항을 파악해 최적의 도입 방안을 안내드립니다.' },
  { step: '02', title: '도메인 설정', desc: '전용 서브도메인(yourcompany.kdist.net)과 관리자 계정을 즉시 발급합니다.' },
  { step: '03', title: '데이터 마이그레이션', desc: '기존 권리사·아티스트·앨범 데이터를 엑셀 일괄 업로드로 빠르게 이전합니다.' },
  { step: '04', title: '운영 시작', desc: '로그인 즉시 대시보드, 정산 리포트, AI 분석 등 모든 기능을 사용할 수 있습니다.' },
];

const CHECKS = [
  [Globe, '전용 서브도메인 — yourcompany.kdist.net'],
  [Shield, '권리사별 데이터 완전 분리 · 보안'],
  [TrendingUp, '플랫폼별 매출 트렌드 시각화'],
  [LayoutDashboard, '직관적 대시보드 — 별도 교육 불필요'],
  [Disc3, '음원 + 음반 통합 정산 지원'],
  [Mic2, '아티스트·앨범·곡 마스터 데이터 관리'],
  [Music, '음원 심의 신청 및 결과 관리'],
  [FileSpreadsheet, '엑셀 대량 등록 · 정산서 자동 발송'],
];

export default function KDistLandingPage() {
  const [activeFeature, setActiveFeature] = useState(0);
  const [userInteracted, setUserInteracted] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // 파비콘 설정
  useEffect(() => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none"><rect width="32" height="32" rx="8" fill="#0ea5e9"/><rect x="5" y="19" width="5" height="8" rx="1.5" fill="white" fill-opacity="0.55"/><rect x="13.5" y="14" width="5" height="13" rx="1.5" fill="white"/><rect x="22" y="9" width="5" height="18" rx="1.5" fill="white" fill-opacity="0.55"/><path d="M7.5 19 L16 13.5 L24.5 8.5" stroke="white" stroke-width="1.6" stroke-linecap="round"/><circle cx="7.5" cy="19" r="1.8" fill="white"/><circle cx="16" cy="13.5" r="1.8" fill="white"/><circle cx="24.5" cy="8.5" r="2.2" fill="white"/></svg>`;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.href = `data:image/svg+xml,${encodeURIComponent(svg)}`;
    document.title = 'KDIST — 음원유통사를 위한 정산 SaaS';
    const setMeta = (prop, content) => {
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('property', prop); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('og:title', 'KDIST — 음원유통사를 위한 정산 SaaS');
    setMeta('og:description', '권리사 정산, AI 분석, 음반 관리까지. 음원유통사에 필요한 모든 정산 기능을 하나의 플랫폼에.');
    setMeta('og:site_name', 'KDIST');
  }, []);

  // 자동 슬라이드
  useEffect(() => {
    if (userInteracted) return;
    const id = setInterval(() => setActiveFeature((p) => (p + 1) % FEATURES.length), 3600);
    return () => clearInterval(id);
  }, [userInteracted]);

  const handleFeatureClick = (i) => { setActiveFeature(i); setUserInteracted(true); };
  const scrollTo = (id) => { document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); setMenuOpen(false); };

  const ActiveViz = FEATURES[activeFeature].Viz;

  return (
    <div className="min-h-screen bg-[#080e1a] text-white">

      {/* 헤더 */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/[0.06] bg-[#080e1a]/90 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KDistLogoMark size={30} />
            <span className="font-black text-lg tracking-tight">KDIST</span>
          </div>
          <nav className="hidden sm:flex items-center gap-6 text-sm text-slate-400">
            <button onClick={() => scrollTo('features')} className="hover:text-white transition-colors">기능</button>
            <button onClick={() => scrollTo('how')} className="hover:text-white transition-colors">도입 절차</button>
            <button onClick={() => scrollTo('contact')} className="hover:text-white transition-colors">문의</button>
          </nav>
          <div className="flex items-center gap-3">
            <a href="mailto:master@impink.co.kr"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-400 text-white text-sm font-semibold transition-colors">
              도입 문의
            </a>
            <button className="sm:hidden text-slate-400 space-y-1 p-1" onClick={() => setMenuOpen(!menuOpen)}>
              <div className="w-5 h-0.5 bg-current"/>
              <div className="w-5 h-0.5 bg-current"/>
              <div className="w-5 h-0.5 bg-current"/>
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="sm:hidden border-t border-white/[0.06] bg-[#080e1a] px-4 py-4 space-y-3 text-sm">
            <button onClick={() => scrollTo('features')} className="block w-full text-left text-slate-300 py-1">기능</button>
            <button onClick={() => scrollTo('how')} className="block w-full text-left text-slate-300 py-1">도입 절차</button>
            <button onClick={() => scrollTo('contact')} className="block w-full text-left text-slate-300 py-1">문의</button>
          </div>
        )}
      </header>

      {/* 히어로 */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 max-w-6xl mx-auto text-center overflow-hidden">
        {/* 점 그리드 배경 */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(rgba(14,165,233,0.12) 1px, transparent 1px)', backgroundSize: '28px 28px' }}/>
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(14,165,233,0.08) 0%, transparent 70%)' }}/>
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-400 text-xs font-semibold mb-7">
            <Zap size={11} /> 음원유통사 전용 정산 SaaS 플랫폼
          </div>
          <h1 className="text-4xl sm:text-6xl font-black leading-[1.1] tracking-tight mb-6">
            권리사 정산,<br />
            <span className="text-sky-400">더 스마트하게.</span>
          </h1>
          <p className="text-slate-400 text-lg sm:text-xl max-w-xl mx-auto leading-relaxed mb-10">
            권리사 관리부터 정산 리포트, AI 분석, 음반 정산까지<br className="hidden sm:block" />
            음원유통에 필요한 모든 기능을 하나의 플랫폼에.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button onClick={() => scrollTo('contact')}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-white font-semibold transition-colors">
              도입 문의하기 <ChevronRight size={16} />
            </button>
            <button onClick={() => scrollTo('features')}
              className="w-full sm:w-auto px-6 py-3.5 rounded-xl border border-white/10 hover:bg-white/[0.05] text-slate-300 font-semibold transition-colors">
              기능 살펴보기
            </button>
          </div>
        </div>
      </section>

      {/* 스탯 바 */}
      <section className="border-y border-white/[0.06] bg-white/[0.015] py-10 px-4">
        <div className="max-w-4xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[['99.9%', '서비스 가동률'], ['실시간', '데이터 반영'], ['무제한', '권리사 등록'], ['AI', '자동 분석']].map(([v, l]) => (
            <div key={l}>
              <div className="text-2xl sm:text-3xl font-black text-sky-400 mb-1">{v}</div>
              <div className="text-xs text-slate-500">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 기능 인터랙티브 쇼케이스 */}
      <section id="features" className="py-24 px-4 sm:px-6 relative">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 80% 40% at 50% 50%, rgba(14,165,233,0.04) 0%, transparent 70%)' }}/>
        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-black mb-3">필요한 기능, 전부 있습니다</h2>
            <p className="text-slate-400">클릭해서 각 기능의 실제 동작을 확인해보세요.</p>
          </div>

          <div className="flex flex-col lg:flex-row gap-5">
            {/* 기능 목록 */}
            <div className="lg:w-64 shrink-0 space-y-1">
              {FEATURES.map((f, i) => {
                const Icon = f.icon;
                const isActive = i === activeFeature;
                return (
                  <button key={i} onClick={() => handleFeatureClick(i)}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-all duration-200"
                    style={{
                      background: isActive ? 'rgba(14,165,233,0.1)' : 'transparent',
                      borderLeft: isActive ? '2px solid #0ea5e9' : '2px solid transparent',
                    }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: isActive ? 'rgba(14,165,233,0.15)' : 'rgba(255,255,255,0.04)' }}>
                      <Icon size={16} className={isActive ? 'text-sky-400' : 'text-slate-500'} />
                    </div>
                    <div>
                      <div className={`text-sm font-semibold ${isActive ? 'text-white' : 'text-slate-400'}`}>{f.title}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* 시각화 패널 */}
            <div className="flex-1 rounded-2xl border border-white/[0.08] bg-[#0a1220] overflow-hidden">
              {/* 가짜 윈도우 크롬 */}
              <div className="px-4 py-3 border-b border-white/[0.06] bg-white/[0.02] flex items-center gap-3">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-rose-500/40"/>
                  <div className="w-3 h-3 rounded-full bg-amber-500/40"/>
                  <div className="w-3 h-3 rounded-full bg-emerald-500/40"/>
                </div>
                <div className="flex-1 text-center">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-white/[0.04] text-[11px] text-slate-500 font-mono">
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/60"/>
                    app.kdist.net/dashboard
                  </div>
                </div>
              </div>

              {/* 기능 헤더 */}
              <div className="px-5 pt-4 pb-3 border-b border-white/[0.04]">
                <div className="flex items-center gap-2.5">
                  {(() => { const Icon = FEATURES[activeFeature].icon; return <Icon size={16} className="text-sky-400"/>; })()}
                  <span className="text-sm font-bold text-white">{FEATURES[activeFeature].title}</span>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">{FEATURES[activeFeature].desc}</p>
              </div>

              {/* 시각화 (key로 리마운트 → 애니메이션 재시작) */}
              <div className="p-5" style={{ minHeight: 220 }}>
                <div key={activeFeature}>
                  <ActiveViz />
                </div>
              </div>

              {/* 인디케이터 */}
              <div className="px-5 pb-4 flex items-center gap-1.5">
                {FEATURES.map((_, i) => (
                  <button key={i} onClick={() => handleFeatureClick(i)}
                    className="h-1 rounded-full transition-all duration-300"
                    style={{ width: i === activeFeature ? 20 : 6, background: i === activeFeature ? '#0ea5e9' : 'rgba(255,255,255,0.12)' }}
                  />
                ))}
                {!userInteracted && (
                  <span className="ml-auto text-[10px] text-slate-600">자동 재생 중</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why KDIST */}
      <section className="py-16 px-4 sm:px-6 border-y border-white/[0.05] bg-white/[0.01]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black text-center mb-10">왜 KDIST인가요?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CHECKS.map(([Icon, text]) => (
              <div key={text} className="flex items-center gap-3 p-4 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:border-sky-500/20 transition-colors">
                <CheckCircle2 size={15} className="text-sky-400 shrink-0"/>
                <span className="text-sm text-slate-300">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 도입 절차 */}
      <section id="how" className="py-24 px-4 sm:px-6 max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-black mb-3">빠른 도입, 쉬운 시작</h2>
          <p className="text-slate-400">복잡한 설치 없이 4단계로 운영을 시작합니다.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {HOW_IT_WORKS.map((h, i) => (
            <div key={h.step}>
              <div className="w-12 h-12 rounded-2xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center mb-4">
                <span className="text-sky-400 font-black">{h.step}</span>
              </div>
              <h3 className="font-bold text-white mb-2">{h.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{h.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="py-24 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto text-center">
          <div className="rounded-3xl border border-sky-500/20 bg-gradient-to-b from-sky-500/[0.06] to-transparent p-10 sm:p-14">
            <h2 className="text-3xl sm:text-4xl font-black mb-4">지금 도입 문의하세요</h2>
            <p className="text-slate-400 mb-8 leading-relaxed">
              서비스 규모와 요구사항을 알려주시면<br />
              최적의 도입 방안을 안내드립니다.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="mailto:master@impink.co.kr"
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-white font-semibold transition-colors">
                <Mail size={16} />
                master@impink.co.kr
              </a>
              <a href="tel:1844-2549"
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-white/10 hover:bg-white/[0.05] text-slate-300 font-semibold transition-colors">
                1844-2549
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 푸터 */}
      <footer className="border-t border-white/[0.05] py-8 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-600">
          <div className="flex items-center gap-2">
            <KDistLogoMark size={20} />
            <span className="font-bold text-slate-500">KDIST</span>
            <span>— Powered by p!nk</span>
          </div>
          <div>© 2024 핑크페이퍼. All rights reserved.</div>
        </div>
      </footer>

    </div>
  );
}
